/*
 * Purpose: Site + user analytics dashboard — admin gated
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RefreshCw, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ContextualError } from '@/components/errors/ContextualError';
import { fetchAnalyticsSummary } from '@/lib/adminApi';
import { getAnalyticsConsent, setAnalyticsConsent } from '@/lib/analyticsConsent';
import type { AnalyticsSummary } from '@/lib/siteAnalyticsTypes';
import { APP_ROUTES } from '@/lib/appRoutes';

export function SiteAnalyticsPanel() {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(getAnalyticsConsent());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await fetchAnalyticsSummary(14));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const chartData =
    summary?.daily.map((d) => ({
      ...d,
      label: d.date.slice(5),
    })) ?? [];

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Site & user analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Aggregated events from consenting users — no vault secrets.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {consent !== 'accepted' && (
        <ContextualError
          message="Analytics cookies are not accepted on this browser — traffic may be under-reported."
          action={{
            label: 'Accept analytics',
            onClick: () => {
              setAnalyticsConsent('accepted');
              setConsent('accepted');
            },
          }}
          secondaryAction={{ label: 'AI insights', href: APP_ROUTES.adminAiInsights }}
        />
      )}

      {error && (
        <ContextualError
          message={error}
          action={{ label: 'Retry', onClick: () => void load() }}
          secondaryAction={{ label: 'Admin home', href: APP_ROUTES.admin }}
        />
      )}

      {summary && (
        <>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <Metric title="Page views" value={summary.totals.pageViews} />
            <Metric title="Sessions" value={summary.totals.sessions} />
            <Metric title="Errors" value={summary.totals.errors} />
            <Metric title="Events sampled" value={summary.eventCount} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Daily traffic</CardTitle>
              <CardDescription>Page views, sessions, and errors — last 14 days</CardDescription>
            </CardHeader>
            <CardContent className="h-64 sm:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="pageViews" stroke="#10b981" name="Views" strokeWidth={2} />
                  <Line type="monotone" dataKey="sessions" stroke="#3b82f6" name="Sessions" strokeWidth={2} />
                  <Line type="monotone" dataKey="errors" stroke="#ef4444" name="Errors" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top paths</CardTitle>
              </CardHeader>
              <CardContent className="h-56">
                {summary.topPaths.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No page views yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summary.topPaths.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="path" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="hsl(var(--ab-green-core))" radius={4} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" /> Active users
                </CardTitle>
                <CardDescription>By event volume (hashed email or anon id)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 max-h-56 overflow-y-auto">
                {summary.topUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No user events yet.</p>
                ) : (
                  summary.topUsers.map((u) => (
                    <div key={u.userId} className="flex items-center justify-between text-sm gap-2">
                      <span className="truncate font-mono text-xs">{u.userId}</span>
                      <Badge variant="secondary">{u.events} events</Badge>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent errors</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-48 overflow-y-auto">
              {summary.recentErrors.length === 0 ? (
                <p className="text-sm text-muted-foreground">No error events recorded.</p>
              ) : (
                summary.recentErrors.map((e, i) => (
                  <div key={i} className="text-xs border-b border-border/40 pb-2 last:border-0">
                    <span className="text-muted-foreground">{e.meta?.message as string ?? e.path}</span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Metric({ title, value }: { title: string; value: number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-xl">{value.toLocaleString()}</CardTitle>
      </CardHeader>
    </Card>
  );
}
